/** Shared, frozen inputs and direction helpers: the bot never allocates an input per scan. */

import { DIR_DOWN, DIR_LEFT, DIR_NONE, DIR_RIGHT, DIR_UP, DX, DY } from '../sim';
import type { Dir, Input } from '../sim';

/** The four moving directions, in the order the bot tries alternatives. */
export const DIRS: readonly Dir[] = [DIR_UP, DIR_RIGHT, DIR_DOWN, DIR_LEFT];

const ALL: readonly Dir[] = [DIR_NONE, DIR_UP, DIR_RIGHT, DIR_DOWN, DIR_LEFT];
const MOVES: readonly Input[] = ALL.map((dir) => Object.freeze({ dir, fire: false }));
const SNAPS: readonly Input[] = ALL.map((dir) => Object.freeze({ dir, fire: true }));

/** No direction, no fire. */
export const STAY: Input = MOVES[DIR_NONE] ?? Object.freeze({ dir: DIR_NONE, fire: false });

export function moveIn(dir: Dir): Input {
  return MOVES[dir] ?? STAY;
}

/** Grab the cell in `dir` without moving. */
export function snapIn(dir: Dir): Input {
  return SNAPS[dir] ?? STAY;
}

export function isHorizontal(dir: Dir): boolean {
  return dir === DIR_LEFT || dir === DIR_RIGHT;
}

/** The neighbour of `cell` in `dir`, or -1 outside a `w` x `h` grid. */
export function stepCell(cell: number, dir: Dir, w: number, h: number): number {
  const x = (cell % w) + (DX[dir] ?? 0);
  const y = Math.floor(cell / w) + (DY[dir] ?? 0);
  if (x < 0 || y < 0 || x >= w || y >= h) return -1;
  return y * w + x;
}

export function manhattan(a: number, b: number, w: number): number {
  return Math.abs((a % w) - (b % w)) + Math.abs(Math.floor(a / w) - Math.floor(b / w));
}

export { DIR_LEFT, DIR_RIGHT, DIR_UP };
